import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { hatDeckblatt, readBodyOoxml } from "@/office/word/einfuegen";
import { MAKRO_BAUSTEINE } from "../makros";

export const lektionDeckblatt: Lesson = {
  id: "deckblatt-schnellbausteine",
  title: "Deckblätter und Schnellbausteine",
  summary: "Fertige Bausteine einfügen – und eigene Textbausteine für immer wiederkehrende Inhalte anlegen.",
  durationMinutes: 9,
  learningGoals: [
    "Ein Deckblatt aus dem Katalog einfügen und füllen",
    "Einen eigenen Schnellbaustein speichern und wiederverwenden",
    "Schnellbausteine, AutoText und AutoKorrektur unterscheiden",
  ],
  steps: [
    {
      id: "deckblatt",
      title: "Ein Deckblatt einfügen",
      explanation: [
        "„Einfügen“ → „Deckblatt“ setzt eine fertig gestaltete Titelseite vor das Dokument – " +
          "unabhängig davon, wo der Cursor gerade steht. Die Felder darauf sind " +
          "Inhaltssteuerelemente, die teils mit den Dokumenteigenschaften verbunden sind: " +
          "Wer den Titel auf dem Deckblatt einträgt, trägt ihn gleichzeitig unter „Datei“ → " +
          "„Informationen“ ein.",
        "Ein Deckblatt bekommt automatisch „Erste Seite anders“ – Kopf- und Fußzeile samt " +
          "Seitenzahl bleiben auf ihm leer. Ein erneutes Einfügen ersetzt das alte Deckblatt, " +
          "statt ein zweites anzulegen.",
      ],
      keyPoints: [
        "Einfügen → Deckblatt – immer auf Seite 1.",
        "Die Felder sind mit den Dokumenteigenschaften verbunden.",
        "Einfügen → Deckblatt → „Aktuelles Deckblatt entfernen“.",
      ],
      task: {
        instruction: "Füge ein Deckblatt aus dem Katalog ein und trage mindestens den Titel ein.",
        hint: "Einfügen → Deckblatt → eine Variante wählen, dann auf „[Dokumenttitel]“ klicken und tippen.",
        check: async () => {
          const deckblatt = hatDeckblatt(await readBodyOoxml());
          return fromCriteria([criterion("Ein Deckblatt aus dem Katalog ist eingefügt", deckblatt)], {
            success: "Das Deckblatt steht – mit leerer Kopf- und Fußzeile auf Seite 1.",
            failure: "Es gibt noch kein Deckblatt aus dem Katalog.",
            hint: "Eine selbst gestaltete erste Seite zählt nicht – nutze Einfügen → Deckblatt.",
          });
        },
      },
    },
    {
      id: "schnellbaustein",
      title: "Einen eigenen Schnellbaustein anlegen",
      explanation: [
        "Absender, Bankverbindung, der Standardabsatz zum Datenschutz: Was in vielen " +
          "Dokumenten gleich lautet, gehört in einen Schnellbaustein. Text markieren, " +
          "„Einfügen“ → „Schnellbausteine“ → „Auswahl im Schnellbaustein-Katalog speichern“, " +
          "Namen vergeben.",
        "Eingefügt wird er über denselben Katalog – oder schneller: Namen tippen und F3 " +
          "drücken. Formatierung, Tabellen und Bilder bleiben erhalten.",
        "Word speichert Schnellbausteine standardmäßig in „Building Blocks.dotx“, einer " +
          "Datei im Benutzerprofil. Damit stehen sie in allen Dokumenten zur Verfügung. Im " +
          "Team gehören gemeinsame Bausteine in die gemeinsame Dokumentvorlage – das Thema " +
          "des Moduls „Vorlagen“.",
      ],
      keyPoints: [
        "Einfügen → Schnellbausteine → Auswahl im Katalog speichern.",
        "Name tippen, F3 drücken.",
        "Gespeichert in Building Blocks.dotx – für alle Dokumente verfügbar.",
      ],
      task: {
        instruction:
          "Speichere einen Textbaustein (zum Beispiel deine Grußformel mit Namen) unter dem " +
          "Namen „Kursbaustein“ im Schnellbaustein-Katalog.",
        hint: "Text markieren, Einfügen → Schnellbausteine → „Auswahl im Schnellbaustein-Katalog speichern“, Name: Kursbaustein.",
        macro: MAKRO_BAUSTEINE,
        check: () =>
          pruefeMitMakro(MAKRO_BAUSTEINE, (bericht) => {
            const gefunden = bericht.janein("gefunden") === true;
            return fromCriteria(
              [criterion("Ein Baustein namens „Kursbaustein“ ist gespeichert", gefunden)],
              {
                success: `Gespeichert${bericht.text("katalog") ? ` im Katalog „${bericht.text("katalog")}“` : ""}. Ab jetzt: „Kursbaustein“ tippen, F3.`,
                failure: "Ein Baustein namens „Kursbaustein“ ist noch nicht gespeichert.",
                hint: "Auf die genaue Schreibweise des Namens achten.",
              },
            );
          }),
      },
    },
    {
      id: "abgrenzung",
      title: "Schnellbaustein, AutoText, AutoKorrektur",
      explanation: [
        "Drei Werkzeuge ersetzen Tipparbeit, und sie werden oft verwechselt. " +
          "Schnellbausteine sind formatierte Inhalte, die man bewusst einfügt – über den " +
          "Katalog oder F3. AutoText ist ein eigener Katalog im selben System, dessen " +
          "Einträge Word beim Tippen des Namens zusätzlich vorschlägt.",
        "Die AutoKorrektur ersetzt ohne Nachfrage, sobald ein Kürzel getippt ist – gedacht " +
          "für Tippfehler und kurze Ersetzungen wie „mfg“. Wie man eigene Einträge anlegt, " +
          "zeigt das Modul „Ansichten, Eigenschaften und Makros“.",
      ],
      keyPoints: [
        "Schnellbaustein: bewusst einfügen, formatiert.",
        "AutoText: Vorschlag beim Tippen.",
        "AutoKorrektur: ersetzt sofort und ungefragt.",
      ],
    },
  ],
};
